<template>
  <div class="admin-page">
    <h2>Пользователи</h2>

    <p v-if="error" class="error-message">{{ error }}</p>

    <div class="panel">
      <p v-if="loading" class="empty-text">Загрузка...</p>
      <p v-else-if="users.length === 0" class="empty-text">Пользователей нет.</p>

      <div v-else class="admin-table-wrap">
        <table class="admin-table">
          <thead>
            <tr>
              <th>Пользователь</th>
              <th>Роль</th>
              <th>Блокировка</th>
              <th>Регистрация</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="user in users" :key="user.id">
              <td data-label="Пользователь">
                <div class="admin-cell-user">
                  <UserIdentity :user="user" size="sm" />
                  <span class="admin-cell-muted">{{ user.email }}</span>
                </div>
              </td>
              <td data-label="Роль">{{ userRoleLabel(user.role) }}</td>
              <td data-label="Блокировка">
                <span v-if="user.blocked">
                  Да, {{ formatDateTime(user.blocked_at) }}
                </span>
                <span v-else>Нет</span>
              </td>
              <td data-label="Регистрация">{{ formatDateTime(user.created_at) }}</td>
              <td data-label="Действия">
                <div v-if="isSelf(user)" class="admin-cell-muted">Это вы</div>
                <div v-else class="btn-row admin-actions">
                  <button
                    v-if="!user.blocked"
                    type="button"
                    class="btn btn-secondary btn-sm"
                    :disabled="busyId === user.id"
                    @click="blockUser(user)"
                  >
                    Заблокировать
                  </button>
                  <button
                    v-else
                    type="button"
                    class="btn btn-secondary btn-sm"
                    :disabled="busyId === user.id"
                    @click="unblockUser(user)"
                  >
                    Разблокировать
                  </button>
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm"
                    :disabled="busyId === user.id"
                    @click="removeComments(user)"
                  >
                    Удалить комментарии
                  </button>
                  <button
                    type="button"
                    class="btn btn-secondary btn-sm"
                    :disabled="busyId === user.id"
                    @click="removeCommentsAndBlock(user)"
                  >
                    Комментарии + блок
                  </button>
                  <button
                    type="button"
                    class="btn btn-danger btn-sm"
                    :disabled="busyId === user.id"
                    @click="removeUser(user)"
                  >
                    Удалить
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import UserIdentity from '../../components/UserIdentity.vue'
import {
  blockAdminUser,
  destroyAdminUser,
  destroyUserComments,
  destroyUserCommentsAndBlock,
  fetchAdminUsers,
  unblockAdminUser,
} from '../../api/admin'
import { useAuthStore } from '../../stores/auth'
import { extractApiError } from '../../utils/apiError'
import { formatDateTime } from '../../utils/formatDateTime'
import { userRoleLabel } from '../../utils/moderationLabels'

const auth = useAuthStore()
const users = ref([])
const loading = ref(true)
const error = ref('')
const busyId = ref(null)

function isSelf(user) {
  return auth.user?.id === user.id
}

async function loadUsers() {
  loading.value = true
  error.value = ''
  try {
    users.value = await fetchAdminUsers()
  } catch (err) {
    error.value = extractApiError(err, 'Не удалось загрузить пользователей')
  } finally {
    loading.value = false
  }
}

function updateUser(updated) {
  users.value = users.value.map((item) => (item.id === updated.id ? updated : item))
}

async function runAction(user, action) {
  busyId.value = user.id
  error.value = ''
  try {
    await action()
  } catch (err) {
    error.value = extractApiError(err)
  } finally {
    busyId.value = null
  }
}

async function blockUser(user) {
  if (!window.confirm(`Заблокировать пользователя ${user.display_name || user.email}?`)) return
  await runAction(user, async () => {
    const updated = await blockAdminUser(user.id)
    updateUser(updated)
  })
}

async function unblockUser(user) {
  if (!window.confirm(`Разблокировать пользователя ${user.display_name || user.email}?`)) return
  await runAction(user, async () => {
    const updated = await unblockAdminUser(user.id)
    updateUser(updated)
  })
}

async function removeComments(user) {
  if (!window.confirm(`Удалить все комментарии пользователя ${user.display_name || user.email}?`)) return
  await runAction(user, async () => {
    await destroyUserComments(user.id)
  })
}

async function removeCommentsAndBlock(user) {
  if (
    !window.confirm(
      `Удалить все комментарии и заблокировать пользователя ${user.display_name || user.email}?`
    )
  ) {
    return
  }
  await runAction(user, async () => {
    await destroyUserCommentsAndBlock(user.id)
    await loadUsers()
  })
}

async function removeUser(user) {
  if (!window.confirm(`Удалить пользователя ${user.display_name || user.email}? Это необратимо.`)) return
  await runAction(user, async () => {
    await destroyAdminUser(user.id)
    users.value = users.value.filter((item) => item.id !== user.id)
  })
}

onMounted(loadUsers)
</script>
